import { datadogRum } from "@datadog/browser-rum";

/**
 * Browser-reported signals for whether the device is actually rendering.
 *
 * Passive by design: these observe low-end Chromebooks and must not cost frames.
 * See docs/frontend-render-health.md.
 */

// Capped per page load (this counter resets with the module): these fire on devices already
// struggling, and long frames arrive in bursts.
const MAX_REPORTS_PER_KIND = 10;

// Lesson boot on a school Chromebook routinely produces 300-800ms tasks; 1s is the anomaly line.
const LONG_FRAME_BLOCKING_MS = 1000;

const reportCounts: Record<string, number> = {};

const isEnabled = () => import.meta.env.VITE_DATADOG_ENABLED === "true";

const report = (kind: string, context: Record<string, unknown>) => {
  if (!isEnabled()) return;
  const seen = reportCounts[kind] ?? 0;
  if (seen >= MAX_REPORTS_PER_KIND) return;
  reportCounts[kind] = seen + 1;
  try {
    datadogRum.addAction(`render_health.${kind}`, {
      ...context,
      occurrence: seen + 1,
      capped: seen + 1 === MAX_REPORTS_PER_KIND,
    });
  } catch {
    // Best effort only.
  }
};

/** Run one signal's setup; an unsupported API must not break app boot. */
const guard = (fn: () => void) => {
  try {
    fn();
  } catch {
    // Best effort only.
  }
};

/** GPU renderer string, via a throwaway context released immediately. */
// The canvas stays detached: loseContext() below fires the event observeContextLoss reports,
// and a detached node gives it no path to the window listener.
const readGpuRenderer = (): string | null => {
  const canvas = document.createElement("canvas");
  const gl = (canvas.getContext("webgl") ||
    canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
  if (!gl) return null;
  const info = gl.getExtension("WEBGL_debug_renderer_info");
  const renderer = info
    ? (gl.getParameter(info.UNMASKED_RENDERER_WEBGL) as string)
    : gl.getParameter(gl.RENDERER);
  const lose = gl.getExtension("WEBGL_lose_context");
  lose?.loseContext();
  return typeof renderer === "string" ? renderer : null;
};

/** Attach hardware class to the session so every signal can be sliced by device. */
const setDeviceContext = () => {
  const nav = navigator as Navigator & {
    deviceMemory?: number;
    hardwareConcurrency?: number;
  };
  datadogRum.setGlobalContextProperty("device_profile", {
    device_memory_gb: nav.deviceMemory ?? null,
    hardware_concurrency: nav.hardwareConcurrency ?? null,
    device_pixel_ratio: window.devicePixelRatio ?? null,
    gpu_renderer: readGpuRenderer(),
  });
};

/** Frames that blocked past the threshold or never reached their render phase. */
const observeLongAnimationFrames = () => {
  if (
    !PerformanceObserver.supportedEntryTypes?.includes("long-animation-frame")
  )
    return;

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      const frame = entry as PerformanceEntry & {
        blockingDuration?: number;
        renderStart?: number;
        styleAndLayoutStart?: number;
        scripts?: Array<{ name?: string; invoker?: string; duration?: number }>;
      };
      const blocking = frame.blockingDuration ?? 0;
      const neverRendered = !frame.renderStart;
      if (blocking < LONG_FRAME_BLOCKING_MS && !neverRendered) continue;

      const worst = (frame.scripts ?? [])
        .slice()
        .sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0))[0];

      report("long_animation_frame", {
        duration_ms: Math.round(frame.duration),
        blocking_duration_ms: Math.round(blocking),
        // 0 means the frame was abandoned before rendering.
        render_start_ms: Math.round(frame.renderStart ?? 0),
        never_rendered: neverRendered,
        style_and_layout_start_ms: Math.round(frame.styleAndLayoutStart ?? 0),
        worst_script: worst?.name ?? worst?.invoker ?? null,
        worst_script_ms: worst?.duration ? Math.round(worst.duration) : null,
      });
    }
  });
  observer.observe({ type: "long-animation-frame", buffered: true });
};

/** Browser interventions and crash reports. */
const observeReports = () => {
  const ReportingObserverCtor = (
    window as unknown as { ReportingObserver?: typeof ReportingObserver }
  ).ReportingObserver;
  if (!ReportingObserverCtor) return;

  const observer = new ReportingObserverCtor(
    (reports) => {
      for (const entry of reports) {
        const body = entry.body as unknown as Record<string, unknown> | null;
        report("browser_report", {
          report_type: entry.type,
          url: entry.url,
          message: (body?.message as string) ?? null,
          source_file: (body?.sourceFile as string) ?? null,
        });
      }
    },
    // Deprecations are build-time noise and would burn the per-session cap.
    { types: ["intervention", "crash"], buffered: true },
  );
  observer.observe();
};

/** WebGL context loss, captured at the window so canvases mounted later are covered. */
const observeContextLoss = () => {
  window.addEventListener(
    "webglcontextlost",
    (event) => {
      const target = event.target as HTMLElement | null;
      report("webgl_context_lost", {
        target_tag: target?.tagName ?? null,
        target_id: target?.id || null,
        target_class: target?.className?.toString().slice(0, 120) || null,
      });
    },
    true,
  );

  window.addEventListener(
    "webglcontextrestored",
    () => report("webgl_context_restored", {}),
    true,
  );
};

/** Tab freeze, resume, and restore-after-discard. */
const observeLifecycle = () => {
  if ((document as Document & { wasDiscarded?: boolean }).wasDiscarded) {
    report("tab_restored_after_discard", {});
  }
  document.addEventListener("freeze", () => report("tab_frozen", {}), true);
  document.addEventListener(
    "resume",
    () =>
      report("tab_resumed", {
        visibility_state: document.visibilityState,
      }),
    true,
  );
};

/** Call once at boot; no-ops when RUM is disabled. */
export function initRenderHealth(): void {
  if (!isEnabled()) return;
  guard(setDeviceContext);
  guard(observeLongAnimationFrames);
  guard(observeReports);
  guard(observeContextLoss);
  guard(observeLifecycle);
}
